import { PaginationInfo } from "src/components/tailwind-components/Pagination"
// Re-export shared auto-extraction types from the form feature
export type {
  CreditContractPreparationAutoExtraction,
  CreditContractPreparationDocumentExtraction,
  CreditContractPreparationDocumentExtractedField,
} from "src/features/credit-contract-preparation-form/hooks/useCreditContractPreparation"
import type { CreditContractPreparationAutoExtraction } from "src/features/credit-contract-preparation-form/hooks/useCreditContractPreparation"

// APIステータス型（APIの仕様に合わせて追加）
export type CreditContractPreparationStatus =
  | "action_required" // 要対応
  | "revoked" // 取り下げ済み
  | "completed" // 対応済み

// クレジット契約準備作業の審査状況（API仕様に合わせて更新）
export type ExaminationStatus =
  | "有効期限切れ" // 有効期限切れ
  | "再審査中" // 再審査中
  | "通過" // 通過
  | "否認" // 否認

// クレジット契約準備作業のデータ型（API仕様に合わせて更新）
export interface CreditContractPreparation {
  id: number // API: id (integer)
  proposalId: number // API: proposalId (integer)
  userId: number // API: userId (integer)
  userName: string // API: userName (string)
  paymentInitialCostOn: string // API: paymentInitialCostOn (date)
  storeName: string | null // API: storeName (string, nullable)
  examinationStatus: ExaminationStatus // API: examinationStatus (string)
  status: CreditContractPreparationStatus // API: status (string)
  memo: string | null // API: memo (string, nullable)
  smootherName: string | null // API: smootherName (string, nullable)
  storeHoliday: string | null // API: storeHoliday (string, nullable)
  updatedAt: string // API: updatedAt (date-time)
  createdAt: string // API: createdAt (date-time)
  autoExtraction?: CreditContractPreparationAutoExtraction | null
}

// フィルタ用のパラメータ型
export interface UseCreditContractPreparationsParams {
  statuses?: CreditContractPreparationStatus[]
  page?: number
}

// フック戻り値型の拡張
export interface UseCreditContractPreparationsReturn {
  data: CreditContractPreparation[]
  pagination: PaginationInfo
  isLoading: boolean
  error: Error | undefined
  refresh: () => Promise<CreditContractPreparationListResponse | undefined>
}

// API レスポンス型
export interface CreditContractPreparationListResponse {
  status: number
  creditContractPreparations: CreditContractPreparation[]
  pagination: PaginationInfo
}

// リストコンポーネントのプロパティ型
export interface CreditContractPreparationListProps {
  defaultStatuses: CreditContractPreparationStatus[]
}

// テーブルのプロパティ型
export interface CreditContractPreparationTableProps {
  data: CreditContractPreparation[]
  pagination: PaginationInfo
  isLoading?: boolean
  onPageChange: (page: number) => void
  onStatusUpdate?: () => Promise<void>
}

// テーブル行のプロパティ型
export interface CreditContractPreparationTableRowProps {
  item: CreditContractPreparation
  onStatusUpdate?: () => Promise<void>
}

export type CreditContractPreparationDocumentExtractionStatus =
  | "pending"
  | "processing"
  | "succeeded"
  | "failed"

// 審査状況のバッジスタイル設定
export interface StatusBadgeConfig {
  label: string
  className: string
}

export const EXAMINATION_STATUS_CONFIG: Record<ExaminationStatus, StatusBadgeConfig> = {
  有効期限切れ: {
    label: "有効期限切れ",
    className: "bg-red-100 text-red-800 border-red-200",
  },
  再審査中: {
    label: "再審査中",
    className: "bg-yellow-100 text-yellow-800 border-yellow-200",
  },
  通過: {
    label: "通過",
    className: "bg-green-100 text-green-800 border-green-200",
  },
  否認: {
    label: "否認",
    className: "bg-gray-100 text-gray-800 border-gray-200",
  },
}

export const CREDIT_CONTRACT_PREPARATION_STATUS_CONFIG: Record<
  CreditContractPreparationStatus,
  StatusBadgeConfig
> = {
  action_required: {
    label: "要対応",
    className: "bg-red-100 text-red-800 border-red-200",
  },
  revoked: {
    label: "取り下げ済み",
    className: "bg-gray-100 text-gray-800 border-gray-200",
  },
  completed: {
    label: "対応済み",
    className: "bg-green-100 text-green-800 border-green-200",
  },
}
